import { Suspense, lazy, useEffect, useState, type ReactElement } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import type { Funksjonsflaggnokkel, Meg } from '@es/shared';
import {
  useBudsjettoppgaver,
  useInvesteringsoppgaver,
  useFunksjonsflagg,
  useMeg,
  useOppgaver,
  useRapportoppgaver,
  useVarseloppgaver,
  useAksjonsoppgaver,
  useKontrolloppgaver,
} from './api/sporringer.js';
import { erBareFolkevalgt } from './sider/rapportering/behandling.js';
import { serBudsjettet } from './sider/budsjett/budsjett.js';
import { serInvesteringene } from './sider/investeringer/investeringer.js';
import { hentBruker } from './auth/oidc.js';
import { Ramme, type Lenke } from './komponenter/Ramme.js';
import { Virksomhetsvelger } from './felles/virksomhet.js';
import { Feilmelding, Laster } from './komponenter/Tilstand.js';
import { Feilgrense } from './komponenter/Feilgrense.js';
import { AuthTilbake } from './sider/AuthTilbake.js';
import { Innlogging } from './sider/Innlogging.js';
import { Oversikt } from './sider/Oversikt.js';
import { Sak } from './sider/Sak.js';

/**
 * Feature modules are loaded on demand.
 *
 * Each is a substantial screen, and a kommune that has only Finansiell oversikt
 * switched on should not pay to download Prognose. It also keeps the initial
 * bundle inside the size budget in vite.config.ts — a budget that exists because
 * this runs on ordinary municipal desktops, not developer laptops.
 */
const FinansiellOversikt = lazy(() =>
  import('./sider/FinansiellOversikt.js').then((m) => ({ default: m.FinansiellOversikt })),
);
const Prognose = lazy(() =>
  import('./sider/prognose/Prognose.js').then((m) => ({ default: m.Prognose })),
);
const Kpi = lazy(() => import('./sider/kpi/Kpi.js').then((m) => ({ default: m.Kpi })));
const MoterOgGrupper = lazy(() =>
  import('./sider/moter/MoterOgGrupper.js').then((m) => ({ default: m.MoterOgGrupper })),
);
const Prosjekter = lazy(() =>
  import('./sider/prosjekter/Prosjekter.js').then((m) => ({ default: m.Prosjekter })),
);
const Investeringer = lazy(() =>
  import('./sider/investeringer/Investeringer.js').then((m) => ({ default: m.Investeringer })),
);
const Tiltaksoppfolging = lazy(() =>
  import('./sider/kpi/Tiltaksoppfolging.js').then((m) => ({ default: m.Tiltaksoppfolging })),
);
const Bemanning = lazy(() =>
  import('./sider/bemanning/Bemanning.js').then((m) => ({ default: m.Bemanning })),
);
const Administrasjon = lazy(() =>
  import('./sider/administrasjon/Administrasjon.js').then((m) => ({ default: m.Administrasjon })),
);
const Innkjop = lazy(() =>
  import('./sider/innkjop/Innkjop.js').then((m) => ({ default: m.Innkjop })),
);
const Rapportering = lazy(() =>
  import('./sider/rapportering/Rapportering.js').then((m) => ({ default: m.Rapportering })),
);
const Budsjett = lazy(() =>
  import('./sider/budsjett/Budsjett.js').then((m) => ({ default: m.Budsjett })),
);
const Importer = lazy(() =>
  import('./sider/import/Import.js').then((m) => ({ default: m.Importer })),
);

/**
 * The feature modules, each gated on its own flag (§2).
 *
 * One list drives both the navigation AND the routes, so a module that is not
 * switched on for a kommune has neither — a hand-typed URL lands on the
 * not-found page rather than on a screen whose API calls would be refused anyway.
 */
const MODULER: readonly {
  /** Modulen vises hvis minst ett av flaggene er på. */
  flagg: Funksjonsflaggnokkel | readonly Funksjonsflaggnokkel[];
  lenke: Lenke;
  element: ReactElement;
  kunAdministrator?: boolean;
  /**
   * Skjuler modulen for den som bare er `leser`.
   *
   * Bemanningen skal vise lønn per ansatt, og innsyn hører hos leder og økonom.
   * Serveren nekter uansett — en meny som skjuler en rute er ingen
   * tilgangskontroll — men å tilby en lenke som garantert gir 403 er å love noe
   * vi ikke holder.
   */
  kunLederEllerOkonom?: boolean;
  /**
   * Vises også for den som bare er folkevalgt. Alle andre moduler skjules for
   * dem: de har ingen virksomhet, og ville bare fått tomme sider.
   */
  forFolkevalgte?: boolean;
  /**
   * Vises bare for den dette gjelder. Budsjettet er for dem som setter rammene
   * og dem som svarer på dem; for alle andre ville siden bare sagt nei.
   */
  bareFor?: (meg: Meg) => boolean;
}[] = [
  {
    flagg: 'finansiell_oversikt',
    lenke: { til: '/finansiell-oversikt', tekst: 'Finansiell oversikt' },
    element: <FinansiellOversikt />,
  },
  {
    flagg: 'prognose',
    lenke: { til: '/prognose', tekst: 'Prognose' },
    element: <Prognose />,
  },
  {
    // Én modul, to uavhengige kilder. Den vises hvis MINST ETT av flaggene er
    // på, og hver seksjon inne på siden gates på sitt eget — en kommune som
    // bare har regnskapsimport ser bare den.
    flagg: ['import_regnskap', 'bemanning'],
    lenke: { til: '/import', tekst: 'Importer' },
    element: <Importer />,
  },
  {
    flagg: 'rapportering',
    lenke: { til: '/rapportering', tekst: 'Rapportering' },
    element: <Rapportering />,
    forFolkevalgte: true,
  },
  {
    flagg: 'budsjett',
    lenke: { til: '/budsjett', tekst: 'Budsjett' },
    element: <Budsjett />,
    bareFor: serBudsjettet,
  },
  {
    flagg: 'kpi_og_tiltak',
    lenke: { til: '/kpi', tekst: 'KPI og tiltak' },
    element: <Kpi />,
  },
  {
    flagg: 'kpi_og_tiltak',
    lenke: { til: '/tiltaksoppfolging', tekst: 'Tiltaksoppfølging' },
    element: <Tiltaksoppfolging />,
  },
  {
    flagg: 'prosjekter',
    lenke: { til: '/prosjekter', tekst: 'Prosjekter' },
    element: <Prosjekter />,
  },
  {
    flagg: 'investeringer',
    lenke: { til: '/investeringer', tekst: 'Investeringer' },
    element: <Investeringer />,
    bareFor: serInvesteringene,
  },
  {
    flagg: 'moter',
    lenke: { til: '/moter', tekst: 'Møter og grupper' },
    element: <MoterOgGrupper />,
  },
  {
    flagg: 'bemanning',
    lenke: { til: '/bemanning', tekst: 'Bemanning' },
    element: <Bemanning />,
    kunLederEllerOkonom: true,
  },
  {
    flagg: 'innkjop',
    lenke: { til: '/innkjop', tekst: 'Innkjøp' },
    element: <Innkjop />,
  },
  {
    flagg: 'administrasjon',
    lenke: { til: '/administrasjon', tekst: 'Administrasjon' },
    element: <Administrasjon />,
    // Even with the flag on, the menu entry is for administrators only: the list
    // of who can see what is itself sensitive.
    kunAdministrator: true,
  },
];

function Beskyttet(): ReactElement {
  const [innlogget, settInnlogget] = useState<boolean | null>(null);
  // Nøkkelen som nullstiller feilgrensen når brukeren navigerer bort fra en
  // skjerm som feilet. Uten den blir feilmeldingen stående etter klikket, og
  // det ser ut som om navigasjonen heller ikke virker.
  const { pathname: sti } = useLocation();

  useEffect(() => {
    let avbrutt = false;
    void hentBruker().then((bruker) => {
      if (!avbrutt) settInnlogget(bruker !== null);
    });
    return () => {
      avbrutt = true;
    };
  }, []);

  const meg = useMeg();
  const flagg = useFunksjonsflagg();
  // En entreprenør har bare vedlikeholdsappen, og skal ikke spørre etter
  // kommunens oppgaver — det ville bare gitt avslag.
  const intern = innlogget === true && meg.isSuccess && meg.data.entreprenor === null;
  const harPrognose =
    intern && flagg.data?.some((f) => f.nokkel === 'prognose' && f.aktivert) === true;
  const oppgaver = useOppgaver(harPrognose);
  const varseloppgaver = useVarseloppgaver(harPrognose);
  const aksjonsoppgaver = useAksjonsoppgaver(
    intern && flagg.data?.some((f) => f.nokkel === 'moter' && f.aktivert) === true,
  );
  const rapportoppgaver = useRapportoppgaver(
    intern && flagg.data?.some((f) => f.nokkel === 'rapportering' && f.aktivert) === true,
  );
  const investeringsoppgaver = useInvesteringsoppgaver(
    intern &&
      serInvesteringene(meg.data) &&
      flagg.data?.some((f) => f.nokkel === 'investeringer' && f.aktivert) === true,
  );
  const kontrolloppgaver = useKontrolloppgaver(
    intern && flagg.data?.some((f) => f.nokkel === 'vedlikehold' && f.aktivert) === true,
  );
  const budsjettoppgaver = useBudsjettoppgaver(
    intern && flagg.data?.some((f) => f.nokkel === 'budsjett' && f.aktivert) === true,
  );

  if (innlogget === null) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <Laster hva="økten" />
      </main>
    );
  }
  if (!innlogget) return <Innlogging />;

  if (meg.isPending) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <Laster hva="brukeren din" />
      </main>
    );
  }

  if (meg.isError) {
    // Authenticated at the broker but with no access here — the API's 403. Shown
    // as a plain explanation rather than a login loop, which is what the
    // prototype learned to do.
    return (
      <main className="mx-auto max-w-lg px-6 py-16">
        <h1 className="text-xl font-semibold text-tekst">Ingen tilgang</h1>
        <div className="mt-4">
          <Feilmelding feil={meg.error} />
        </div>
      </main>
    );
  }

  if (meg.data.entreprenor !== null) return <TilVedlikehold />;

  // A module the kommune has not been switched on for gets no navigation entry
  // AND no route — so a hand-typed URL lands on the not-found page rather than
  // on a screen whose API calls would be rejected anyway.
  const erPa = (nokkel: Funksjonsflaggnokkel): boolean =>
    flagg.data?.some((f) => f.nokkel === nokkel && f.aktivert) === true;

  // Rollene er per virksomhet, så spørsmålet er om kalleren er leder eller
  // økonom NOE sted. Er hun det bare ett sted, er modulen meningsfull — den
  // viser uansett én virksomhet om gangen, og serveren avgjør hvilke.
  const erLederEllerOkonom = meg.data.tilganger.some(
    (t) => t.rolle === 'leder' || t.rolle === 'okonom',
  );

  const bareFolkevalgt = erBareFolkevalgt(meg.data);
  const aktive = MODULER.filter(
    (m) =>
      (Array.isArray(m.flagg) ? m.flagg.some(erPa) : erPa(m.flagg as Funksjonsflaggnokkel)) &&
      (m.kunAdministrator !== true || meg.data.kanAdministrere) &&
      (m.kunLederEllerOkonom !== true || erLederEllerOkonom) &&
      (!bareFolkevalgt || m.forFolkevalgte === true) &&
      (m.bareFor === undefined || m.bareFor(meg.data)),
  );

  return (
    // Provideren ligger UTENFOR Ramme, fordi velgeren i skallet og skjermene
    // under må dele den samme tilstanden.
    <Virksomhetsvelger>
      <Ramme
        brukerNavn={meg.data.navn}
        kommuneNavn={meg.data.kommuneNavn}
        lenker={[
          { til: '/', tekst: 'Oversikt' },
          // Tallet står ved modulen oppgavene gjelder.
          ...aktive.map((m) =>
            m.lenke.til === '/prognose'
              ? {
                  ...m.lenke,
                  antall:
                    (oppgaver.data?.oppgaver.length ?? 0) +
                    (varseloppgaver.data?.oppgaver.length ?? 0),
                }
              : m.lenke.til === '/rapportering'
                ? { ...m.lenke, antall: rapportoppgaver.data?.oppgaver.length ?? 0 }
                : m.lenke.til === '/budsjett'
                  ? { ...m.lenke, antall: budsjettoppgaver.data?.oppgaver.length ?? 0 }
                  : m.lenke.til === '/moter'
                    ? { ...m.lenke, antall: aksjonsoppgaver.data?.oppgaver.length ?? 0 }
                    : m.lenke.til === '/investeringer'
                      ? { ...m.lenke, antall: investeringsoppgaver.data?.prosjekter.length ?? 0 }
                      : m.lenke,
          ),
          ...(erPa('vedlikehold') && !bareFolkevalgt
            ? [
                {
                  til: '/vedlikehold',
                  tekst: 'Vedlikehold',
                  egenApp: true,
                  antall: kontrolloppgaver.data?.kontroller.length ?? 0,
                },
              ]
            : []),
        ]}
      >
        {/* INNENFOR Ramme, med vilje. Feiler én skjerm, står menyen og
            virksomhetsvelgeren igjen, og brukeren kan gå et annet sted uten å
            laste noe på nytt. Utenfor ville hele skallet forsvunnet med den.

            Og UTENFOR Suspense: en `lazy`-modul som ikke lar seg hente — et
            bygg som er rullet videre mens fanen sto åpen — avviser løftet, og
            den feilen går til nærmeste feilgrense. Lå grensen innenfor, ville
            det fortsatt endt som en hvit side. */}
        <Feilgrense omfang="siden" nullstillNokkel={sti}>
          <Suspense fallback={<Laster hva="siden" />}>
            <Routes>
              <Route path="/" element={<Oversikt />} />
              {aktive.map((m) => (
                <Route key={m.lenke.til} path={m.lenke.til} element={m.element} />
              ))}
              {/* Sakene fra modulene som er på: aksjonspunktene og tiltakene. */}
              <Route
                path="/sak/:type/:id"
                element={
                  <Sak
                    typer={[
                      ...(erPa('moter') && !bareFolkevalgt ? (['aksjonspunkt'] as const) : []),
                      ...(erPa('kpi_og_tiltak') && !bareFolkevalgt ? (['tiltak'] as const) : []),
                    ]}
                  />
                }
              />
              <Route path="*" element={<IkkeFunnet />} />
            </Routes>
          </Suspense>
        </Feilgrense>
      </Ramme>
    </Virksomhetsvelger>
  );
}

function IkkeFunnet(): ReactElement {
  return (
    <>
      <h1 className="text-xl font-semibold text-tekst">Siden finnes ikke</h1>
      <p className="mt-2 text-sm text-tekst-dempet">
        Adressen finnes ikke, eller modulen er ikke aktivert for kommunen din.
      </p>
    </>
  );
}

export function App(): ReactElement {
  return (
    <Routes>
      <Route path="/auth/tilbake" element={<AuthTilbake />} />
      <Route path="*" element={<Beskyttet />} />
    </Routes>
  );
}

/**
 * En entreprenør har arbeidsordrene i vedlikeholdsappen og ingenting her.
 * Den er en egen side, så det er en ny innlasting, ikke en rute.
 */
function TilVedlikehold(): ReactElement {
  useEffect(() => {
    window.location.replace('/vedlikehold/');
  }, []);
  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <Laster hva="arbeidsordrene" />
    </main>
  );
}
